/**
 * Shared "exact vs estimate" vocabulary for the presentational components (design 12.3: "Counters
 * are exact and say so, estimates say 'est.'").
 *
 * Several components (the dense table, the sparkline, chart legends) need to show a number AND say
 * whether it is an exact counter, a sampled estimate, or an unverifiable value. Rather than each
 * component inventing its own flag, they share this one small type and the `formatMeasured` /
 * `estimateSuffix` helpers so the wording and the "est." affordance are identical everywhere.
 *
 * This is screen-agnostic: a screen (tasks 11.4+) decides, from the data-quality context, whether a
 * given number is exact or estimated and passes that down; the component only renders what it is
 * told. "Unverifiable" is distinct from "estimated": an estimate is a known-sampled number, an
 * unverifiable value is one the control plane could not confirm — and per design 12.3 it is NEVER
 * shown as healthy/green (the status components enforce the colour; this module only labels it).
 */

/** How confident we are in a displayed number. */
export type Measure = "exact" | "estimate" | "unverifiable";

/** A number (or pre-formatted string) carrying its confidence so a cell/legend can label it. */
export interface MeasuredValue {
  /** The display value. A string is used verbatim; a number is left to the caller/`format`. */
  readonly value: number | string;
  /** Confidence in the value; defaults to `exact` when omitted by a caller. */
  readonly measure?: Measure;
}

/**
 * The short suffix a number carries for its confidence. Exact numbers get nothing (they are the
 * common case and design 12.3 says counters simply "are exact"); an estimate gets " est."; an
 * unverifiable value is spelled out so it is never mistaken for a real figure.
 */
export function estimateSuffix(measure: Measure | undefined): string {
  switch (measure) {
    case "estimate":
      return " est.";
    case "unverifiable":
      return " (unverifiable)";
    case "exact":
    case undefined:
      return "";
  }
}

/** A longer, screen-reader-friendly phrase for the confidence, e.g. for a cell's accessible name. */
export function measureDescription(measure: Measure | undefined): string {
  switch (measure) {
    case "estimate":
      return "estimated (sampled)";
    case "unverifiable":
      return "unverifiable";
    case "exact":
    case undefined:
      return "exact";
  }
}

/**
 * Format a {@link MeasuredValue} to a display string, applying `format` to a numeric value and
 * appending the confidence suffix. A string value is used as-is (the caller already formatted it).
 */
export function formatMeasured(
  measured: MeasuredValue,
  format: (value: number) => string = defaultNumberFormat,
): string {
  const text = typeof measured.value === "number" ? format(measured.value) : measured.value;
  return `${text}${estimateSuffix(measured.measure)}`;
}

/** A locale-grouped integer-ish default used when a caller does not supply its own formatter. */
export function defaultNumberFormat(value: number): string {
  if (!Number.isFinite(value)) return "n/a";
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
